/**
 * KeyboardAwareScrollView — pure-RN scroll-to-focused-input, no native module.
 *
 * When a TextInput inside this component receives focus the scroll view
 * scrolls so the input's centre lands at roughly the vertical midpoint of
 * the visible area above the keyboard, making typing comfortable.
 *
 * Architecture
 * ───────────────────────────────────────────────────────────────────────
 * A React context (`KeyboardAwareContext`) exposes a single callback:
 *
 *   onInputFocus(inputRef: React.RefObject<View | TextInput>): void
 *
 * Any input inside the tree calls this on focus.  The context is
 * populated by the component itself, so callers never need to wire
 * individual inputs — the shared `Input` component reads the context and
 * calls `onInputFocus` automatically when mounted inside here.
 *
 * Scroll calculation
 * ───────────────────────────────────────────────────────────────────────
 *   visibleHeight  = screenHeight − keyboardHeight − safeAreaInsets.bottom
 *   targetY        = inputY − (visibleHeight/2 − inputHeight/2)
 *                  = inputY − visibleHeight/2 + inputHeight/2
 *
 * Clamped to [0, contentHeight - visibleHeight] so we never
 * over-scroll past either end.
 *
 * Platform notes
 * ───────────────────────────────────────────────────────────────────────
 * • iOS:   keyboardWillShow fires before the keyboard is visible, giving
 *          a smooth simultaneous scroll + keyboard rise animation.
 * • Android: keyboardWillShow does not fire; keyboardDidShow is used.
 * • Web:   Keyboard.addListener works on react-native-web but the
 *          keyboard height comes back as 0 on desktop browsers.  The
 *          component degrades gracefully — it still scrolls the input
 *          into view (just not to the mid-point above a non-existent
 *          keyboard).  On mobile browsers the visual-viewport resize
 *          is handled by the browser's native scroll-to-input; the
 *          component's scroll just adds fine-grained centering.
 * • No crash path: all measure calls are guarded; if a ref is stale or
 *   measureInWindow is unavailable the scroll is silently skipped.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Keyboard,
  Platform,
  ScrollView,
  type ScrollViewProps,
  useWindowDimensions,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { computeKeyboardScrollTarget } from '../../lib/keyboardScroll'

// ─── Context ────────────────────────────────────────────────────────────────

interface KeyboardAwareContextValue {
  /**
   * Call this from a TextInput's onFocus handler with the ref to the
   * input (or any wrapping View you want to scroll to).  The scroll
   * view will center the ref in the visible area above the keyboard.
   */
  onInputFocus: (ref: React.RefObject<View | null>) => void
}

const KeyboardAwareContext = createContext<KeyboardAwareContextValue | null>(null)

/**
 * Hook consumed by Input to register focus with the nearest
 * KeyboardAwareScrollView ancestor.  Returns null when not nested
 * inside one — callers should treat null as a no-op.
 */
export function useKeyboardAware(): KeyboardAwareContextValue | null {
  return useContext(KeyboardAwareContext)
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface KeyboardAwareScrollViewProps extends ScrollViewProps {
  children: React.ReactNode
  /** Receives the inner ScrollView, for callers that scroll it themselves. */
  scrollRef?: React.MutableRefObject<ScrollView | null>
}

export function KeyboardAwareScrollView({
  children,
  scrollRef: externalRef,
  ...scrollProps
}: KeyboardAwareScrollViewProps) {
  const { height: screenHeight } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const scrollRef = useRef<ScrollView>(null)

  // Keyboard height tracking — iOS uses Will (before keyboard animates),
  // Android uses Did (only reliable event).
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const keyboardHeightRef = useRef(0)

  // Tracks the latest scroll content height so we can clamp scroll position.
  const contentHeightRef = useRef(0)
  // Current scroll offset — updated via onScroll so we always know where we are.
  const scrollOffsetRef = useRef(0)
  // The most recently focused input. Re-scrolled when the keyboard height
  // finally arrives (see the show listener) — this is what keeps the input
  // above the keyboard on Android.
  const focusedRef = useRef<React.RefObject<View | null> | null>(null)

  // Core scroll math: centre the given input in the visible area above the
  // keyboard. Reads keyboard height / scroll offset from refs at call time, so
  // it's correct whether invoked on focus or after the keyboard has shown.
  const performScroll = useCallback(
    (inputRef: React.RefObject<View | null>) => {
      const node = inputRef.current
      const scroll = scrollRef.current
      if (!node || !scroll) return

      // measureInWindow gives coordinates relative to the *screen*. We also
      // need the scroll view's own screen-space origin to convert to
      // scroll-content coordinates.
      ;(node as View).measureInWindow((x, inputScreenY, _w, inputHeight) => {
        if (inputHeight === 0 && inputScreenY === 0) return // stale ref

        ;(scroll as unknown as View).measureInWindow(
          (_sx, scrollViewScreenY, _sw, scrollViewScreenHeight) => {
            if (scrollViewScreenHeight === 0) return // stale ref

            const clampedY = computeKeyboardScrollTarget({
              inputScreenY,
              inputHeight,
              scrollViewScreenY,
              scrollViewScreenHeight,
              keyboardHeight: keyboardHeightRef.current,
              insetBottom: insets.bottom,
              screenHeight,
              scrollOffset: scrollOffsetRef.current,
              contentHeight: contentHeightRef.current,
            })

            scroll.scrollTo({ y: clampedY, animated: true })
          },
        )
      })
    },
    [screenHeight, insets.bottom],
  )

  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow'
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide'

    const showSub = Keyboard.addListener(showEvent, (e) => {
      const h = e.endCoordinates.height
      setKeyboardHeight(h)
      keyboardHeightRef.current = h
      // Re-centre the focused input now that the REAL keyboard height is
      // known. On Android keyboardDidShow frequently fires AFTER the focus
      // handler's timer, so the focus-time scroll ran with height 0 and left
      // the input behind the keyboard — this corrective scroll fixes it.
      if (focusedRef.current) {
        const ref = focusedRef.current
        setTimeout(() => performScroll(ref), 16)
      }
    })
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0)
      keyboardHeightRef.current = 0
      focusedRef.current = null
    })

    return () => {
      showSub.remove()
      hideSub.remove()
    }
  }, [performScroll])

  const onInputFocus = useCallback(
    (inputRef: React.RefObject<View | null>) => {
      focusedRef.current = inputRef
      // Early attempt once layout settles. The show listener re-scrolls with
      // the correct keyboard height if it arrives later (Android).
      const delay = Platform.OS === 'android' ? 100 : 50
      setTimeout(() => performScroll(inputRef), delay)
    },
    [performScroll],
  )

  const contextValue = React.useMemo(
    () => ({ onInputFocus }),
    [onInputFocus],
  )

  return (
    <KeyboardAwareContext.Provider value={contextValue}>
      <ScrollView
        ref={(node) => {
          scrollRef.current = node
          if (externalRef) externalRef.current = node
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={(e) => {
          scrollOffsetRef.current = e.nativeEvent.contentOffset.y
          scrollProps.onScroll?.(e)
        }}
        onContentSizeChange={(w, h) => {
          contentHeightRef.current = h
          scrollProps.onContentSizeChange?.(w, h)
        }}
        {...scrollProps}
      >
        {children}
      </ScrollView>
    </KeyboardAwareContext.Provider>
  )
}
